import {
    AbstractMonitoredEventPanel,
    AbstractMonitoredEventPanelProps,
    AbstractMonitoredEventPanelState,
} from 'app/components/panels/predict/AbstractMonitoredEventPanel';
import { OpsInstruction } from 'app/model/SOL49';
import { ManagedColumn } from 'app/common/table/ManagedTable';
import { ServiceFactory } from 'app/service/ServiceFactory';
import { MonitoredEventUtils } from 'app/common/util/MonitoredEventUtils';
import { Divider, notification, Spin } from 'antd';
import { OpsInstructionToolbar } from 'app/components/dashboard/global/events/MonitoredEventToolbar';
import React from 'react';
import OpsInstructionsTable, {
    OpsInstructionsColumns,
} from 'app/components/panels/predict/opsInstrusctions/OpsInstructionsTable';
import KeyUtils from 'app/common/util/KeyUtils';
import { OpsInstructionsForm } from 'app/components/panels/predict/opsInstrusctions/OpsInstructionsForm';
import Utils from 'app/common/util/Utils';
import { Flex } from 'app/components/common/Flex';
import { SidePanelSeparator } from 'app/components/common/SidePanelSeparator';
import { PDFViewer } from 'app/components/common/PDFViewer';

export interface OpsInstructionPanelProps extends AbstractMonitoredEventPanelProps<OpsInstruction> {}

interface OpsInstructionPanelState extends AbstractMonitoredEventPanelState<OpsInstruction> {
    filter: string;
}

export class OpsInstructionsPanel extends AbstractMonitoredEventPanel<
    OpsInstruction,
    OpsInstructionPanelProps,
    OpsInstructionPanelState
> {
    protected columns: ManagedColumn<OpsInstruction>[] = OpsInstructionsColumns;

    protected getToolbar(): React.ReactNode {
        return (
            <OpsInstructionToolbar
                textFilter={this.state.filter}
                showTags={true}
                onTextFilterChange={textFilter => this.setState({ filter: textFilter.toUpperCase() })}
                settings={this.props.settings}
                onSettings={this.onSettingsChange}
            />
        );
    }

    protected getDefaultEvent(): OpsInstruction {
        return {};
    }

    protected getForm(event: OpsInstruction): React.ReactNode {
        return (
            <OpsInstructionsForm
                event={event}
                onCreate={this.onCreate}
                onUpdate={this.onUpdate}
                onDelete={this.onDelete}
            />
        );
    }

    protected getTable(events: OpsInstruction[], selected: OpsInstruction): React.ReactNode {
        return (
            <OpsInstructionsTable
                data={events}
                columns={OpsInstructionsColumns}
                keyProvider={KeyUtils.MONITORED_EVENT_KEY_PROVIDER}
                noDataMessage={'No ops instructions'}
                selection={this.selection(selected)}
            />
        );
    }

    protected refresh = () => {
        ServiceFactory.sol49Service()
            .getMonitoredEvents({})
            .then(reply => this.setState({ events: reply.data.filter(MonitoredEventUtils.isOpsInstruction) }))
            .catch(err => notification.error({ message: 'Error fetching events', description: '' + err }))
            .finally(() => this.setState({ loading: false }));
    };

    protected filterEvents(events: OpsInstruction[]): OpsInstruction[] {
        events = super.filterEvents(events);
        return Utils.isEmptyString(this.state.filter)
            ? events
            : events.filter(e => e.title.toUpperCase().includes(this.state.filter));
    }

    render() {
        let events = this.filterEvents(this.state.events);
        events = this.sortEvents(events);
        const selected = events.find(note => note.id === this.state.selected);
        const defaultEvent = selected || this.getDefaultEvent();

        return (
            <Spin spinning={this.state.loading}>
                <Flex style={{ minHeight: '75em' }}>
                    <Flex direction="vertical" style={{ padding: '0.5em' }} grow={false} size={'50em'}>
                        {this.getToolbar()}
                        <Flex style={{ minHeight: '25em' }}>{this.getTable(events, selected)}</Flex>
                        <Divider style={{ width: '100%', alignSelf: 'stretch' }} />
                        <Flex grow={false} className="monitored-event-form">
                            {this.getForm(defaultEvent)}
                        </Flex>
                    </Flex>
                    <SidePanelSeparator hideSeparator={true} />
                    <Flex>
                        <PDFViewer file={defaultEvent?.pdf} />
                    </Flex>
                </Flex>
            </Spin>
        );
    }
}
